<script setup lang="ts">
const { onClose } = defineProps<{
  onClose: () => void;
}>();

const { t } = useI18n({
  useScope: "global"
});

const scroll = inject("modalBodyScroll") as ReturnType<typeof useScroll>;

const submitting = ref(false);

const userTermsModal = reactive({
  loading: true,
  error: null as unknown,
  result: null as { url?: string } | null
});

const onFormLoad = async () => {
  try {
    userTermsModal.loading = true;
    const client = useClient();
    const result = await client.user.termsPreview.mutate();
    userTermsModal.result = result;
    userTermsModal.loading = false;
  } catch (e) {
    console.error(e);
    userTermsModal.loading = false;
    userTermsModal.error = e;
  }
};

onMounted(() => {
  onFormLoad();
});

const onSubmit = async (formData: unknown, node: { setErrors: (e: string[]) => void }) => {
  const authStore = useAuthUser();
  try {
    node.setErrors([]);
    submitting.value = true;
    const client = useClient();
    await client.user.termsAccept.mutate();
    await authStore.reload();
    window.location.reload();
    submitting.value = false;
  } catch (e: unknown) {
    node.setErrors([e instanceof Error ? e.message : String(e)]);
    submitting.value = false;
    return false;
  }
};

const abortWithLogout = async () => {
  onClose();
  const authStore = useAuthUser();
  await authStore.logout();
};
</script>

<template>
  <template v-if="userTermsModal.loading">
    <UiLoader style="width: 100%; height: 400px" />
  </template>
  <template v-else-if="userTermsModal.error">
    <div>
      <div class="error">
        {{ userTermsModal.error }}
      </div>
      <FormKit
        type="submit"
        :label="t('modal.userTerms.logout')"
        @click="abortWithLogout"
      />
    </div>
  </template>
  <template v-else>
    <div class="user-terms">
      <div class="terms">
        <ClientOnly>
          <AppPdfPreview :link="userTermsModal.result?.url" />
        </ClientOnly>
      </div>
      <div class="footer">
        <FormKit
          id="user-terms"
          v-slot="{ value }"
          type="form"
          :actions="false"
          @submit="onSubmit"
        >
          <FormKit
            :disabled="!scroll?.arrivedState.bottom"
            name="terms"
            outer-class="checkbox"
            type="checkbox"
            :label="t('modal.userTerms.agreement')"
          />
          <FormKit
            type="group"
            variant="inline"
          >
            <FormKit
              type="button"
              variant="outline"
              :label="t('modal.userTerms.logout')"
              @click="abortWithLogout"
            />
            <FormKit
              :disabled="!value?.terms || submitting"
              type="submit"
              :label="t('modal.userTerms.submit')"
            />
          </FormKit>
        </FormKit>
      </div>
    </div>
  </template>
</template>

<style lang="scss" scoped>
.user-terms {
  position: relative;
  min-height: 40vh;
  max-width: 100%;

  .terms {
    max-width: 100%;
    overflow-x: auto;
  }

  .footer {
    position: sticky;
    bottom: 0;
    z-index: 1;
    width: 100%;
    background-color: #fff;
    padding: 10px 15px;
  }
}
</style>
